import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Spin,
  DatePicker,
  Divider
} from 'antd';
import {
  graphsDateRangeAtom,
  notificationsAtom
} from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { H1 } from '../../../shared-components/typography/Title';
import Card from '../../../shared-components/container/Card';
import {
  getDatePresets,
  thousand,
  toCurrency,
} from '../../../../functions';
import api from '../../../../api/api';
import BatteryBrandsGraph from './shared-components/BatteryBrandsGraph';
import TopSettersGraph from './shared-components/TopSettersGraph';
import TopClosersGraph from './shared-components/TopClosersGraph';
import PayoutReport from './shared-components/PayoutReport';
import DealTypesGraph from './shared-components/DealTypesGraph';

function Dashboard({ includeBack }) {
  const { RangePicker } = DatePicker;
  const history = useHistory();
  const { orgReferralCode } = useParams();
  const { t } = useTranslation();
  const [graphsDateRange, setGraphsDateRange] = useRecoilState(graphsDateRangeAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [organization, setOrganization] = useState(null);
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  function getSummary() {
    setLoading(true);
    let url = 'reports/summary';
    if (organization) {
      url += `?orgReferralCode=${organization.referralCode}`;
    }
    api.get(url)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSummary(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function getOrganization() {
    setLoadingOrganization(true);
    api.get(`organizations?rc=${orgReferralCode}`)
      .then((response) => {
        setLoadingOrganization(false);
        if (response?.data?.success) {
          if (response.data.data) {
            setOrganization(response.data.data);
            getSummary();
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: t('organization_was_not_found'),
                error: true,
              },
            ]);
            history.push('/admin');                
          }
        } else {
          setOrganization(null);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: t('organization_was_not_found'),
              error: true,
            },
          ]);
          history.push('/admin');
        }
      })
      .catch((error) => {
        setLoadingOrganization(false);
        history.push('/admin');
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: t('organization_was_not_found'),
            error: true,
          },
        ]);
      });
  }

  useEffect(() => {
    setGraphsDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('week').format('MMM DD, YYYY')
    });
  }, []);

  useEffect(() => {
    if (orgReferralCode) {
      getOrganization();
    } else {
      getSummary();
    }
  }, [orgReferralCode]);

  return (
    <MainPage includeBack requiresCloserAdmin>
      <Spin spinning={loadingOrganization}>
        {organization && (
          <H1 text={`${t('dashboard')} - ${organization?.nameEn}`} />
        )}
        {!organization && (
          <H1 text={t('global_dashboard')} />
        )}
        <div className="flex mb-2 items-center space-x-2">
          <RangePicker
            className="bg-transparent ml-auto"
            presets={getDatePresets(t)}
            value={[
              dayjs(graphsDateRange?.start),
              dayjs(graphsDateRange?.end)
            ]}
            onChange={(obj) => {
              setGraphsDateRange({
                start: obj[0],
                end: obj[1],
                reload: true
              });
            }}
            format="MMM DD, YYYY"
          />
          <Button
            type="primary"
            size="small"
            onClick={() => setGraphsDateRange({
              ...graphsDateRange,
              reload: true
            })}
          >
            {t('refresh')}
          </Button>
        </div>
        <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('no_appointment')}
              description={thousand(summary?.totalLeadsNoAppointment)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('deals')}
              description={thousand(summary?.totalDeals)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('installs')}
              description={thousand(summary?.totalInstalls)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('total_sytem_size')}
              description={thousand(summary?.systemSizeTotal)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('epc_total')}
              description={toCurrency(summary?.epcTotal)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('closer_commision_total')}
              description={toCurrency(summary?.closerCommisionTotal)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_epc')}
              description={toCurrency(summary?.averageEpc)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_system_size')}
              description={thousand(summary?.averageSystemSize)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_closer_commision')}
              description={toCurrency(summary?.averageCloserCommision)}
            />
          </Spin>
        </div>
        <div className="grid grid-cols-1 mt-4 md:grid-cols-2 gap-4 mb-16">
          <div className="col-span-1">
            <TopClosersGraph filterType={2} orgReferralCode={orgReferralCode} />
          </div>
          <div className="col-span-1 mt-16 md:mt-0">
            <TopClosersGraph filterType={1} orgReferralCode={orgReferralCode} />
          </div>
          <div className="col-span-1 mt-16">
            <TopSettersGraph type={0} filter={0} orgReferralCode={orgReferralCode} />
          </div>
          <div className="col-span-1 mt-16">
            <TopSettersGraph type={0} filter={1} orgReferralCode={orgReferralCode} />
          </div>
          <div className="col-span-1 mt-16">
            <BatteryBrandsGraph orgReferralCode={orgReferralCode} />
          </div>
          <div className="col-span-1 mt-16">
            <DealTypesGraph orgReferralCode={orgReferralCode} />
          </div>
          {/* <div className="col-span-1 md:col-span-2 mt-16">
            <PayoutReport orgReferralCode={orgReferralCode} />
          </div> */}
        </div>
      </Spin>
    </MainPage>
  );
}

export default Dashboard;
